/**
 * Custom hook to access user's region and formatting functions
 */
import { useEffect } from 'react';
import { useAppStore } from '../store/appStore';
import { formatCurrency, getTaxName, getRegionConfig, formatRegionalDate, getDateFormat } from '../lib/regionConfig';

// Cache region in localStorage for instant availability
const REGION_CACHE_KEY = 'user_region';

function getCachedRegion() {
  try {
    return localStorage.getItem(REGION_CACHE_KEY) || 'AU';
  } catch {
    return 'AU';
  }
}

function setCachedRegion(region) {
  try {
    localStorage.setItem(REGION_CACHE_KEY, region);
  } catch {
    // Ignore localStorage errors
  }
}

export function useRegion() {
  const settings = useAppStore((state) => state.settings);
  const settingsLoading = useAppStore((state) => state.settingsLoading);
  
  // Use cached region immediately, then update from settings when loaded
  const cachedRegion = getCachedRegion();
  const region = settings?.region || cachedRegion;

  // Update cache when settings load
  useEffect(() => {
    if (settings?.region && settings.region !== cachedRegion) {
      setCachedRegion(settings.region);
    }
  }, [settings?.region, cachedRegion]);

  return {
    region,
    regionConfig: getRegionConfig(region),
    formatCurrency: (amount) => formatCurrency(amount, region),
    getTaxName: () => getTaxName(region),
    formatDate: (dateString) => formatRegionalDate(dateString, region),
    getDateFormat: () => getDateFormat(region),
    isLoading: settingsLoading,
  };
}
